import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import * as Icons from "lucide-react";
import { PUBLIC_BASE } from "../lib/api";

const KIND_META = {
  viewing: { label: "Viewing request", color: "text-amber-300 bg-amber-500/10 border-amber-400/30", icon: "CalendarCheck" },
  floorplan: { label: "Floor plan", color: "text-emerald-300 bg-emerald-500/10 border-emerald-400/30", icon: "FileText" },
  offer: { label: "Offer", color: "text-rose-300 bg-rose-500/10 border-rose-400/30", icon: "Tag" },
};

const STATUS_META = {
  new: { label: "New", color: "text-rose-300 bg-rose-500/15 border-rose-400/40" },
  contacted: { label: "Contacted", color: "text-amber-300 bg-amber-500/15 border-amber-400/40" },
  closed: { label: "Closed", color: "text-zinc-400 bg-white/5 border-white/15" },
};

/**
 * Realtor-facing inbox embedded INSIDE the public realtor dashboard.
 *
 * Reads/writes via dashboard-token-gated endpoints, matching the existing
 * trust model of /dashboard/{token}/hot-leads.  No login needed — the
 * realtor just bookmarks their dashboard URL.
 *
 *   GET    /api/share/dashboards/{token}/inquiries
 *   GET    /api/share/dashboards/{token}/messages
 *   POST   /api/share/dashboards/{token}/messages/{mid}/reply
 *   PATCH  /api/share/dashboards/{token}/inquiries/{iid}    (status only)
 */
export default function PublicInquiriesPanel({ token, brand }) {
  const [inquiries, setInquiries] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState("inquiries");
  const [loading, setLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [iRes, mRes] = await Promise.all([
        axios.get(`${PUBLIC_BASE}/api/share/dashboards/${token}/inquiries`),
        axios.get(`${PUBLIC_BASE}/api/share/dashboards/${token}/messages`),
      ]);
      setInquiries(iRes.data || []);
      setMessages(mRes.data || []);
    } catch {
      // silent — public endpoint, just keep last successful state
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 45000);
    return () => clearInterval(id);
  }, [refresh]);

  const submitReply = async (mid) => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await axios.post(
        `${PUBLIC_BASE}/api/share/dashboards/${token}/messages/${mid}/reply`,
        { reply: replyText.trim() }
      );
      setReplyingTo(null);
      setReplyText("");
      refresh();
    } catch {
      // surface inline if needed; for now just keep composer open
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (iid, status) => {
    try {
      await axios.patch(
        `${PUBLIC_BASE}/api/share/dashboards/${token}/inquiries/${iid}`,
        { status }
      );
      setInquiries((arr) =>
        arr.map((i) => (i.id === iid ? { ...i, status } : i))
      );
    } catch {}
  };

  const newInquiries = inquiries.filter((i) => (i.status || "new") === "new").length;
  const unrepliedMessages = messages.filter((m) => !m.reply).length;
  const totalNew = newInquiries + unrepliedMessages;

  if (inquiries.length === 0 && messages.length === 0 && !loading) {
    // Don't render an empty panel on a brand-new dashboard
    return null;
  }

  return (
    <div
      className="rounded-2xl border bg-[#0A0A0A]"
      style={{ borderColor: brand ? `${brand}30` : "rgb(255 255 255 / 0.1)" }}
      data-testid="public-inquiries-panel"
    >
      {/* Header bar — clickable to collapse */}
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left"
        data-testid="public-inquiries-toggle"
      >
        <Icons.Inbox size={16} style={{ color: brand || "#a1a1aa" }} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.4em]" style={{ color: brand || "#71717a" }}>
            Inbox
          </div>
          <div className="font-display text-lg text-white">
            {totalNew > 0 ? (
              <>
                {totalNew} new
                <span className="text-zinc-500 text-sm ml-2 font-sans">
                  ({newInquiries} {newInquiries === 1 ? "inquiry" : "inquiries"} · {unrepliedMessages} unanswered {unrepliedMessages === 1 ? "question" : "questions"})
                </span>
              </>
            ) : (
              <>
                All caught up
                <span className="text-zinc-500 text-sm ml-2 font-sans">
                  ({inquiries.length} total)
                </span>
              </>
            )}
          </div>
        </div>
        <Icons.ChevronDown
          size={18}
          className={`text-zinc-500 transition-transform ${collapsed ? "" : "rotate-180"}`}
        />
      </button>

      {!collapsed && (
        <div className="px-5 pb-5 space-y-4">
          {/* Tabs */}
          <div className="flex gap-1 bg-white/5 border border-white/10 rounded-lg p-1 w-fit">
            <button
              onClick={() => setTab("inquiries")}
              className={`px-3 py-1.5 text-xs rounded-md transition-all ${
                tab === "inquiries" ? "bg-white text-black" : "text-zinc-400 hover:text-white"
              }`}
              data-testid="public-inquiries-tab-cta"
            >
              <Icons.Send size={11} className="inline mr-1.5 -mt-0.5" />
              Inquiries
              {inquiries.length > 0 && (
                <span className="ml-1.5 text-[10px] opacity-60">
                  {inquiries.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setTab("messages")}
              className={`px-3 py-1.5 text-xs rounded-md transition-all ${
                tab === "messages" ? "bg-white text-black" : "text-zinc-400 hover:text-white"
              }`}
              data-testid="public-inquiries-tab-questions"
            >
              <Icons.MessageCircle size={11} className="inline mr-1.5 -mt-0.5" />
              Questions
              {messages.length > 0 && (
                <span className="ml-1.5 text-[10px] opacity-60">
                  {messages.length}
                </span>
              )}
            </button>
            <button
              onClick={refresh}
              className="ml-2 text-xs text-zinc-500 hover:text-white px-2 py-1.5"
              data-testid="public-inquiries-refresh"
              title="Refresh"
            >
              <Icons.RefreshCw size={11} className={loading ? "animate-spin" : ""} />
            </button>
          </div>

          {tab === "inquiries" && (
            <div className="space-y-3">
              {inquiries.length === 0 && (
                <EmptyHint
                  icon="Send"
                  text='When viewers tap "Book viewing", "Floor plan" or "Make an offer" on a reel, they show up here.'
                />
              )}
              {inquiries.map((i) => {
                const meta = KIND_META[i.kind] || KIND_META.viewing;
                const statusMeta = STATUS_META[i.status || "new"];
                const I = Icons[meta.icon];
                return (
                  <div
                    key={i.id}
                    className="bg-white/[0.02] border border-white/10 rounded-xl p-4"
                    data-testid={`public-inquiry-${i.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`shrink-0 px-2.5 py-1 rounded-md border text-[10px] uppercase tracking-widest ${meta.color}`}>
                        <I size={10} className="inline mr-1 -mt-0.5" />
                        {meta.label}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white font-medium">
                          {i.name || "Anonymous"}
                          {(i.reel_title || i.listing_address) && (
                            <span className="text-[11px] text-zinc-500 font-normal ml-2">
                              · re: {i.reel_title || i.listing_address}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-zinc-400 mt-1">
                          {i.email && (
                            <a href={`mailto:${i.email}`} className="hover:text-white inline-flex items-center gap-1">
                              <Icons.Mail size={10} /> {i.email}
                            </a>
                          )}
                          {i.phone && (
                            <a href={`tel:${i.phone}`} className="hover:text-white inline-flex items-center gap-1">
                              <Icons.Phone size={10} /> {i.phone}
                            </a>
                          )}
                        </div>
                        {i.preferred_time && (
                          <div className="text-xs text-amber-300 mt-1.5">
                            Prefers: {i.preferred_time}
                          </div>
                        )}
                        {i.message && (
                          <div className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap">
                            {i.message}
                          </div>
                        )}
                        {/* Status pills (clickable) */}
                        <div className="flex items-center gap-1.5 mt-3">
                          <span className="text-[10px] text-zinc-600 mr-1">Status:</span>
                          {Object.entries(STATUS_META).map(([id, m]) => {
                            const active = (i.status || "new") === id;
                            return (
                              <button
                                key={id}
                                onClick={() => setStatus(i.id, id)}
                                className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border transition-all ${
                                  active ? m.color : "text-zinc-500 border-white/10 hover:text-white"
                                }`}
                                data-testid={`public-inquiry-${i.id}-status-${id}`}
                              >
                                {m.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="text-[10px] text-zinc-600 shrink-0">
                        {fmtRelative(i.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tab === "messages" && (
            <div className="space-y-3">
              {messages.length === 0 && (
                <EmptyHint
                  icon="MessageCircle"
                  text="When viewers post questions under a reel, they show up here for you to answer publicly."
                />
              )}
              {messages.map((m) => (
                <div
                  key={m.id}
                  className="bg-white/[0.02] border border-white/10 rounded-xl p-4"
                  data-testid={`public-message-${m.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                      <Icons.MessageCircle size={13} className="text-zinc-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <div className="text-sm text-white font-medium">
                          {m.name || "Visitor"}
                        </div>
                        {m.email && (
                          <a href={`mailto:${m.email}`} className="text-[10px] text-zinc-500 hover:text-white">
                            {m.email}
                          </a>
                        )}
                        {m.reel_title && (
                          <span className="text-[11px] text-zinc-500">
                            · re: {m.reel_title}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-zinc-300 mt-1 whitespace-pre-wrap">
                        {m.body}
                      </div>
                      {m.reply ? (
                        <div className="mt-3 ml-3 pl-3 border-l-2" style={{ borderColor: brand || "#22c55e" }}>
                          <div
                            className="text-[10px] uppercase tracking-widest"
                            style={{ color: brand || "#22c55e" }}
                          >
                            Your reply · {fmtRelative(m.replied_at)}
                          </div>
                          <div className="text-zinc-300 whitespace-pre-wrap mt-1 text-sm">
                            {m.reply}
                          </div>
                        </div>
                      ) : replyingTo === m.id ? (
                        <div className="mt-3 space-y-2">
                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Public reply (visible to all future viewers)…"
                            rows={3}
                            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
                            data-testid={`public-message-${m.id}-reply-input`}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => submitReply(m.id)}
                              disabled={sending || !replyText.trim()}
                              className="text-xs font-medium rounded-md px-3 py-1.5 disabled:opacity-30 inline-flex items-center gap-1"
                              style={{
                                background: brand || "#ffffff",
                                color: brand ? "#ffffff" : "#000000",
                              }}
                              data-testid={`public-message-${m.id}-reply-submit`}
                            >
                              {sending ? (
                                <Icons.Loader2 size={11} className="animate-spin" />
                              ) : (
                                <Icons.Send size={11} />
                              )}
                              Publish reply
                            </button>
                            <button
                              onClick={() => {
                                setReplyingTo(null);
                                setReplyText("");
                              }}
                              className="text-xs text-zinc-500 hover:text-white px-2"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setReplyingTo(m.id);
                            setReplyText("");
                          }}
                          className="mt-2 text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1"
                          data-testid={`public-message-${m.id}-reply-btn`}
                        >
                          <Icons.CornerDownRight size={11} />
                          Reply publicly
                        </button>
                      )}
                    </div>
                    <div className="text-[10px] text-zinc-600 shrink-0">
                      {fmtRelative(m.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyHint({ icon, text }) {
  const I = Icons[icon] || Icons.Inbox;
  return (
    <div className="border border-dashed border-white/10 rounded-xl p-6 text-center">
      <I className="mx-auto text-zinc-700" size={22} />
      <div className="text-sm text-zinc-500 max-w-md mx-auto mt-2">{text}</div>
    </div>
  );
}

function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}
